import { Controller } from "@hotwired/stimulus"

// Emoji picker for the standard composer — the same picker the chat widget uses (emoji-mart). A
// toggle button opens a popover above the toolbar; selecting an emoji inserts its native glyph at
// the cursor in the composer textarea and keeps the picker open for repeated picks. Attach to the
// `.composer` element (alongside any other controller) and give the toggle button
// `data-action="emoji-picker#toggle"`; the textarea is found via `.composer-input`, so no extra
// target wiring is needed on the JS-rendered sandbox composers.
//
// emoji-mart is a heavy dependency, so it is imported lazily the first time the picker opens — that
// keeps it out of the eagerly-globbed main controller bundle that every admin page loads.
export default class extends Controller {
  static targets = ["button"]

  connect() {
    this.popover = null
    this.onDocumentClick = this.onDocumentClick.bind(this)
  }

  disconnect() {
    this.close()
  }

  toggle(event) {
    event.preventDefault()
    if (this.popover) {
      this.close()
    } else {
      this.open()
    }
  }

  async open() {
    const { Picker } = await import("emoji-mart")
    // Bail if we were toggled closed (or another open won the race) while the import was in flight.
    if (this.popover || !this.element.isConnected) return

    const popover = document.createElement("div")
    popover.className = "composer-emoji-popover"

    const picker = new Picker({
      onEmojiSelect: emoji => this.insert(emoji.native),
      perLine: 9,
      maxFrequentRows: 2,
      navPosition: "bottom",
      previewPosition: "none",
      skinTonePosition: "none",
      theme: "light",
      dynamicWidth: false,
    })
    popover.appendChild(picker)
    this.element.appendChild(popover)
    this.popover = popover

    // Defer so the click that opened the picker doesn't immediately close it via the outside handler.
    setTimeout(() => document.addEventListener("click", this.onDocumentClick), 0)
  }

  close() {
    if (!this.popover) return
    document.removeEventListener("click", this.onDocumentClick)
    this.popover.remove()
    this.popover = null
  }

  onDocumentClick(event) {
    if (this.popover.contains(event.target)) return
    if (this.hasButtonTarget && this.buttonTarget.contains(event.target)) return
    this.close()
  }

  insert(text) {
    const input = this.element.querySelector(".composer-input")
    if (!input) return

    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    input.value = input.value.slice(0, start) + text + input.value.slice(end)

    const caret = start + text.length
    input.setSelectionRange(caret, caret)
    input.focus()
    // Let the auto-grow composer-input controller recompute its height.
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
